/**
 * Currency utilities for VA Sprouts
 * Handles currency formatting and conversion across different regions
 */

// Supported currencies with their display configurations
export const SUPPORTED_CURRENCIES = {
  'USD': { symbol: '$', locale: 'en-US', name: 'US Dollar' },
  'EUR': { symbol: '€', locale: 'en-EU', name: 'Euro' },
  'GBP': { symbol: '£', locale: 'en-GB', name: 'British Pound' },
  'CAD': { symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
  'AUD': { symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' },
  'PHP': { symbol: '₱', locale: 'en-PH', name: 'Philippine Peso' },
  'INR': { symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
  'BRL': { symbol: 'R$', locale: 'pt-BR', name: 'Brazilian Real' },
  'MXN': { symbol: 'MX$', locale: 'es-MX', name: 'Mexican Peso' },
  'ZAR': { symbol: 'R', locale: 'en-ZA', name: 'South African Rand' }
};

// Exchange rates (in production, fetch from API or database)
export const EXCHANGE_RATES = {
  'USD': { 'EUR': 0.85, 'GBP': 0.73, 'CAD': 1.25, 'AUD': 1.35, 'PHP': 56, 'INR': 83, 'BRL': 5.0, 'MXN': 18, 'ZAR': 18.5 },
  'EUR': { 'USD': 1.18, 'GBP': 0.86, 'CAD': 1.47, 'AUD': 1.59, 'PHP': 66, 'INR': 98, 'BRL': 5.9, 'MXN': 21, 'ZAR': 22 },
  'GBP': { 'USD': 1.37, 'EUR': 1.16, 'CAD': 1.71, 'AUD': 1.85, 'PHP': 77, 'INR': 114, 'BRL': 6.9, 'MXN': 25, 'ZAR': 25 },
  'CAD': { 'USD': 0.80, 'EUR': 0.68, 'GBP': 0.58, 'AUD': 1.08, 'PHP': 45, 'INR': 66, 'BRL': 4.0, 'MXN': 14, 'ZAR': 15 },
  'AUD': { 'USD': 0.74, 'EUR': 0.63, 'GBP': 0.54, 'CAD': 0.93, 'PHP': 41, 'INR': 61, 'BRL': 3.7, 'MXN': 13, 'ZAR': 14 },
  'PHP': { 'USD': 0.018, 'EUR': 0.015, 'GBP': 0.013, 'CAD': 0.022, 'AUD': 0.024, 'INR': 1.48, 'BRL': 0.089, 'MXN': 0.32, 'ZAR': 0.33 },
  'INR': { 'USD': 0.012, 'EUR': 0.010, 'GBP': 0.0088, 'CAD': 0.015, 'AUD': 0.016, 'PHP': 0.67, 'BRL': 0.060, 'MXN': 0.22, 'ZAR': 0.22 },
  'BRL': { 'USD': 0.20, 'EUR': 0.17, 'GBP': 0.14, 'CAD': 0.25, 'AUD': 0.27, 'PHP': 11.2, 'INR': 16.6, 'MXN': 3.6, 'ZAR': 3.7 },
  'MXN': { 'USD': 0.056, 'EUR': 0.047, 'GBP': 0.040, 'CAD': 0.070, 'AUD': 0.077, 'PHP': 3.1, 'INR': 4.6, 'BRL': 0.28, 'ZAR': 1.03 },
  'ZAR': { 'USD': 0.054, 'EUR': 0.045, 'GBP': 0.040, 'CAD': 0.067, 'AUD': 0.071, 'PHP': 3.0, 'INR': 4.5, 'BRL': 0.27, 'MXN': 0.97 }
};

/**
 * Get exchange rate between two currencies
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {number} Exchange rate
 */
export const getExchangeRate = (fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) return 1;
  return EXCHANGE_RATES[fromCurrency]?.[toCurrency] || 1;
};

/**
 * Format currency amount with proper symbol and locale
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @param {boolean} compact - Whether to use compact notation (e.g., $1.2k)
 * @returns {string} Formatted currency string
 */
export const formatCurrencyAmount = (amount, currency = 'USD', compact = true) => {
  if (!amount || amount === 0) return null;
  
  // Ensure amount is a number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return null;
  
  const config = SUPPORTED_CURRENCIES[currency] || SUPPORTED_CURRENCIES['USD'];
  
  if (compact && numAmount >= 1000) {
    return `${config.symbol}${(numAmount / 1000).toFixed(numAmount % 1000 === 0 ? 0 : 1)}k`;
  }
  
  return `${config.symbol}${Math.round(numAmount).toLocaleString(config.locale)}`;
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (!amount || fromCurrency === toCurrency) return amount;
  const rate = getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
};

/**
 * Format hourly salary range with monthly calculation and currency conversion
 * @param {Object} options - Formatting options
 * @returns {string} Formatted salary string
 */
export const formatHourlySalaryRange = ({
  hourlyMin,
  hourlyMax,
  jobType = 'full_time',
  jobCurrency = 'USD',
  userCurrency = 'USD',
  showConversionNote = true
}) => {
  // Convert string values to numbers and handle null/undefined
  const numHourlyMin = hourlyMin ? parseFloat(hourlyMin) : null;
  const numHourlyMax = hourlyMax ? parseFloat(hourlyMax) : null;
  
  if (!numHourlyMin && !numHourlyMax) return 'TBA';
  
  const exchangeRate = getExchangeRate(jobCurrency, userCurrency);
  const showConversion = showConversionNote && jobCurrency !== userCurrency;
  const conversionNote = showConversion ? ` (converted from ${jobCurrency})` : '';
  
  // Convert hourly amounts
  const convertedHourlyMin = numHourlyMin ? numHourlyMin * exchangeRate : null;
  const convertedHourlyMax = numHourlyMax ? numHourlyMax * exchangeRate : null;
  
  // Calculate monthly equivalent (20 working days * 8 hours for full-time, 4 hours for part-time)
  const hoursPerDay = jobType === 'full_time' ? 8 : 4;
  const daysPerMonth = 20;
  const hoursPerMonth = hoursPerDay * daysPerMonth;
  
  const monthlyMin = convertedHourlyMin ? convertedHourlyMin * hoursPerMonth : null;
  const monthlyMax = convertedHourlyMax ? convertedHourlyMax * hoursPerMonth : null;
  
  // Format monthly range
  const monthlyRange = monthlyMin && monthlyMax 
    ? `${formatCurrencyAmount(monthlyMin, userCurrency)} - ${formatCurrencyAmount(monthlyMax, userCurrency)}`
    : formatCurrencyAmount(monthlyMin || monthlyMax, userCurrency);
  
  return `${monthlyRange}/mo${conversionNote}`;
};

/**
 * Get tooltip text explaining the salary calculation
 * @param {Object} options - Tooltip options
 * @returns {string} Tooltip text
 */
export const getSalaryTooltip = ({
  hourlyMin,
  hourlyMax,
  jobType = 'full_time',
  jobCurrency = 'USD',
  userCurrency = 'USD'
}) => {
  // Convert string values to numbers and handle null/undefined
  const numHourlyMin = hourlyMin ? parseFloat(hourlyMin) : null;
  const numHourlyMax = hourlyMax ? parseFloat(hourlyMax) : null;
  
  if (!numHourlyMin && !numHourlyMax) return '';
  
  const exchangeRate = getExchangeRate(jobCurrency, userCurrency);
  const convertedHourlyMin = numHourlyMin ? numHourlyMin * exchangeRate : null;
  const convertedHourlyMax = numHourlyMax ? numHourlyMax * exchangeRate : null;
  
  const hoursPerDay = jobType === 'full_time' ? 8 : 4;
  const daysPerMonth = 20;
  
  const hourlyRange = convertedHourlyMin && convertedHourlyMax
    ? `${formatCurrencyAmount(convertedHourlyMin, userCurrency)} - ${formatCurrencyAmount(convertedHourlyMax, userCurrency)}`
    : formatCurrencyAmount(convertedHourlyMin || convertedHourlyMax, userCurrency);
  
  const workTypeText = jobType === 'full_time' ? 'full-time' : 'part-time';
  const currencyNote = jobCurrency !== userCurrency ? ` (converted from ${jobCurrency})` : '';
  
  return `Based on ${hourlyRange}/hr for ${workTypeText} work (${hoursPerDay} hrs/day × ${daysPerMonth} days/month)${currencyNote}`;
};

/**
 * Format salary range with currency conversion (legacy compatibility)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted salary string
 */
export const formatSalaryRange = ({
  min,
  max,
  salaryType,
  jobType,
  jobCurrency = 'USD',
  userCurrency = 'USD',
  showConversionNote = true
}) => {
  // If we have hourly rates, use the new hourly formatter
  if (salaryType === 'hourly' || (!salaryType && (min || max))) {
    return formatHourlySalaryRange({
      hourlyMin: min,
      hourlyMax: max,
      jobType,
      jobCurrency,
      userCurrency,
      showConversionNote
    });
  }
  
  // Legacy fallback for other salary types
  if (!min && !max) return 'TBA';
  
  const exchangeRate = getExchangeRate(jobCurrency, userCurrency);
  const showConversion = showConversionNote && jobCurrency !== userCurrency;
  const conversionNote = showConversion ? ` (converted from ${jobCurrency})` : '';
  
  const convertedMin = min ? min * exchangeRate : null;
  const convertedMax = max ? max * exchangeRate : null;
  
  const suffix = salaryType === 'monthly' ? '/mo' : salaryType === 'annual' ? '/yr' : '';
  const range = convertedMin && convertedMax 
    ? `${formatCurrencyAmount(convertedMin, userCurrency)} - ${formatCurrencyAmount(convertedMax, userCurrency)}`
    : formatCurrencyAmount(convertedMin || convertedMax, userCurrency);
  
  return `${range}${suffix}${conversionNote}`;
};